import { maybeWebContext } from "../search";
// chat/brain.ts — the chat brain, SERVER-FIRST since 2026-07-02 (the R8 graduation).
// askBrain now sends the message + this app's declared action catalog to the generic
// fleet chat brain (/api/apps/oriora_closet/chat) — ONE billed call returns ONE
// registered action + a reply; the app executes it locally so the wardrobe stays
// on-device (context sends label metadata only — item/brand/category/colour names;
// never prices, photos, or wear rows). The regex intent-router below is the FREE
// FALLBACK (offline / signed-out / out of funds) — chat never goes dead.
// Original prototype notes:
//
// Deterministic, local-first parsing over THIS app's wardrobe. The ONLY server/AI call
// it can trigger is the app's EXISTING closetRecap (via the "summarize" action in
// actions.ts) — no new endpoints, no vendor SDK, no agent loop.
import {
  store,
  priceToCents,
  normalizeLower,
  DEFAULT_COLORS,
  DEFAULT_CATEGORIES,
  type Garment,
  type GarmentPatch,
} from "../store";
import { BrainError, brainCall } from "../brain";
import { findNamedMatches, notFoundMessage } from "./fuzzy";
import type { ChatAction, ClosetFilter } from "./actions";
import type { FilterRow } from "./actions";
import { libraryFind, librarySummary } from "../library";

export interface ChatTurn { role: "user" | "assistant"; content: string; rows?: FilterRow[]; }
export interface BrainResponse { reply: string; action: ChatAction; }
// The brain can return MULTIPLE actions for one message (e.g. "add these 5 items" → 5 adds), executed
// in order by Chat.tsx. Single-action turns are just a one-element array.
export interface BrainResult { reply: string; actions: ChatAction[]; }

const HELP = "I can add items, log wears, search, rename, summarize, and delete — just tell me.";
const QUERY_RE = /\b(show|find|list|search|which|filter|how\s+many|all|by|what)\b/i;

// The garment's display name for fuzzy matching (item + optional brand).
const garmentName = (g: Garment): string => [g.item, g.brand].filter(Boolean).join(" ");

export async function findByName(term: string): Promise<Garment[]> {
  const q = term.trim().toLowerCase().replace(/[.?!]+$/, "");
  if (!q) return [];
  const all = await store.listGarments(50000);
  const subs = all.filter((g) => g.item.toLowerCase().includes(q) || (g.brand?.toLowerCase().includes(q) ?? false));
  if (subs.length) return subs;
  // Fleet FUZZY standard (fuzzy.ts): forgiving near-match on item/brand so a mistyped or
  // paraphrased name still resolves ("blck jaket" → "black jacket"). Client-side; data on-device.
  const named = findNamedMatches(all.map((g) => ({ ...g, name: garmentName(g) })), term);
  return named.map(({ name: _n, ...g }) => g as Garment);
}

export async function askBrain(history: ChatTurn[], onChunk?: (t: string) => void): Promise<BrainResult> {
  const last = [...history].reverse().find((t) => t.role === "user");
  const text = (last?.content ?? "").trim();
  if (!text) { const r = await routeMessage(text); return { reply: r.reply, actions: [r.action] }; }
  try {
    return await serverChat(history, text, onChunk);
  } catch (e) {
    // A money/auth/key refusal must be SAID, not masked: the local fallback answering a
    // funds-blocked question as if it were a search reads as a broken app (found live 2026-08-27).
    if (e instanceof BrainError && (e.code === "insufficient_funds" || e.code === "unauthenticated" || e.code === "managed_ai_blocked")) {
      return { reply: e.message, actions: [{ kind: "none" }] };
    }
    // Offline / transient server trouble → the free local intent-router below
    // still answers (single action); chat never goes dead.
    const r = await routeMessage(text);
    return { reply: r.reply, actions: [r.action] };
  }
}

// ---- SERVER CHAT-BRAIN (the graduated path) --------------------------------
// The app declares WHAT it can do; the fleet chat brain decides WHICH action the
// message means; this file resolves references (item/brand name → garment id)
// locally, because the wardrobe lives here.

// Standing instructions: the customer shapes the app through chat; stored on-device, sent every call.
const INSTRUCTIONS_KEY = "oriora.closet.instructions";
export const getInstructions = (): string => { try { return localStorage.getItem(INSTRUCTIONS_KEY) ?? ""; } catch { return ""; } };
export const addInstruction = (line: string): void => { try { const cur = getInstructions(); localStorage.setItem(INSTRUCTIONS_KEY, cur ? cur + "\n- " + line : "- " + line); } catch { /* ignore */ } };

export const CHAT_ACTIONS = [
  { name: "add_garment", description: "Add one garment to the wardrobe.", params: { item: "garment name, e.g. \"black leather jacket\"", brand: "optional brand name", category: "optional lowercase category from APP DATA", color: "optional lowercase colour from APP DATA", price_cents: "optional integer purchase price in cents", purchased_on: "optional purchase date YYYY-MM-DD" } },
  { name: "log_wear", description: "Log that the user wore one garment; identify it by its item or brand name.", params: { item: "which garment (item or brand name)" } },
  { name: "edit_garment", description: "Change one existing garment; identify it by its item or brand name.", params: { item: "which garment (item or brand name)", set_item: "optional new name (rename)", set_brand: "optional new brand", set_category: "optional new lowercase category from APP DATA", set_color: "optional new lowercase colour from APP DATA", set_price_cents: "optional corrected integer price in cents", set_purchased_on: "optional corrected purchase date YYYY-MM-DD" } },
  { name: "delete_garment", description: "Delete one garment identified by its item or brand name (the app asks the user to confirm first).", params: { item: "which garment (item or brand name)" } },
  { name: "filter", description: "Search / filter the wardrobe list.", params: { text: "optional free text to match item/brand names", category: "optional category from APP DATA", color: "optional colour from APP DATA", never_worn: "optional true to show only never-worn items", worn: "optional true to show only items worn at least once" } },
  { name: "summarize", description: "Narrate a recap of the user's own logged wardrobe numbers (cost-per-wear, dormancy, category balance). Descriptive only — this app never gives style, shopping, buy/keep/donate, or body advice; use this for recap requests and decline advice requests via 'none'." },
  { name: "backup_save", description: "Back up the WHOLE collection to the customer's own GitHub or Google Drive (connected once in their Oriora account). Works for ANY size — if the backup is too large for the standard path (garment photos), it saves straight from this device to their GitHub when that's logged in (backup menu), or the reply tells them the one-time login to enable it.", params: {} },
  { name: "backup_status", description: "Tell the customer WHERE their data is backed up: which cloud is connected in their Oriora account, which one new backups write to, and when the last backup was. Use for 'where is my data / is my stuff backed up / which cloud do you use'.", params: {} },
  { name: "backup_restore", description: "Restore from the customer's cloud backup — REPLACES current data (the app asks the user to confirm first). If they NAME a source ('restore from Drive'), pass provider.", params: { provider: "optional github|gdrive when the customer names which cloud to restore from" } },
  { name: "save_local_copy", description: "Save a one-off copy of the user's data to a folder/location they pick on THIS device (the OS picker). Works on iPhone and Mac. Use for 'save my data to a file' / 'save a copy locally'.", params: {} },
  { name: "import_file", description: "Import / restore the user's data from a FILE they pick on this device — Files, iCloud, a folder, or a backup from another device. REPLACES current data (the app asks them to confirm first). Use for 'import my data', 'restore from a file', 'load my backup file'.", params: {} },
  { name: "compose_outfit", description: "Recommend / compose outfits for the user from garments they ALREADY own. Use for 'style me', 'what should I wear', 'recommend an outfit', 'put a look together', 'show me a look'.", params: { occasion: "optional occasion / weather / vibe, e.g. \"summer dinner\" or \"cold rainy day\"" } },
  { name: "save_to_cloud", description: "Save the user's data, a note, or THIS conversation to the customer's OWN GitHub or Google Drive. Use when they ask to store/save/keep the conversation, their prompts/messages, or a note.", params: { content_kind: "\"conversation\" to save this whole chat, or \"note\" for a piece of text", note: "the text to save when content_kind is \"note\"", destination: "optional \"github\" or \"gdrive\" if the user names one; omit otherwise" } },
  { name: "remember", description: "Save a standing instruction for how THIS customer wants the app to behave from now on.", params: { instruction: "the preference, one sentence" } },
  // ── ARTIFACT TOOLS — the customer's own library (device-only). Whatever the AI
  // makes for them gets KEPT here instead of vanishing into the chat. Fleet-wide
  // capability, identical in every Oriora app.
  { name: "library_save", description: "Save a plan, summary, guide or note into the customer's ON-DEVICE library. Use whenever you produce something worth keeping, or they ask you to save/remember/write something down. When they ask you to RESEARCH something and make them a plan: write the FULL text in \`content\`, give it a clear title, and put a short summary in your reply.", params: { title: "short title", item_kind: "lesson | plan | note", content: "the FULL text to save — write it out completely", folder: "optional folder name" } },
  { name: "library_list", description: "List what the customer has saved in their library ('what have I saved', 'show my plans', 'what's in my library / in folder X').", params: { folder: "optional folder to filter by" } },
  { name: "library_remove", description: "Remove ONE library item by its title (the app asks the customer to confirm first). LIBRARY items only — the app's own entries have their own delete action.", params: { title: "which item (its title, from LIBRARY in the context)" } },
  { name: "library_move", description: "Move a library item into a folder ('move the summer plan into Plans').", params: { title: "which item", folder: "target folder name" } },
  { name: "library_merge", description: "Merge two or more library items into one (the app asks the customer to confirm first).", params: { titles: "the titles to merge, separated by |", new_title: "title for the merged item" } },
];

// The app's fence, sent to the brain as `scope` — lets ASSIST mode help proactively (build a wardrobe,
// compose outfits from owned pieces) while keeping the App-Store/compliance limits hard.
const CLOSET_SCOPE =
  "Oriora Closet is a personal wardrobe the user builds, organizes and styles. WITHIN that domain you help proactively: add items, suggest/build items to round out the user's OWN closet (name types, colours, styles), add variety, and compose outfits ONLY from garments the user owns. HARD limits — never do these: shopping / where-to-buy / which-brand-to-purchase advice, price or resale opinions, keep/donate/declutter judgments, or any body/appearance/size/figure commentary.";

interface ServerChatItem { action?: string; name?: string; params?: Record<string, unknown> }
interface ServerChat { action?: string; params?: Record<string, unknown>; reply?: string; actions?: ServerChatItem[] }

async function serverChat(history: ChatTurn[], text: string, onChunk?: (t: string) => void): Promise<BrainResult> {
  const all = await store.listGarments(50000);
  const names = all.slice(0, 40).map((g) => (g.brand ? `${g.item} (${g.brand})` : g.item));
  const cats = [...new Set<string>([...DEFAULT_CATEGORIES, ...all.map((g) => normalizeLower(g.category)).filter(Boolean)])];
  const colors = [...new Set<string>([...DEFAULT_COLORS, ...all.map((g) => normalizeLower(g.color)).filter(Boolean)])];
  const _notes = getInstructions();
  const context = (`LIBRARY (their saved plans/notes): ${librarySummary()}. Garments (${all.length} total${all.length > 40 ? ", first 40 shown" : ""}): ${names.join("; ") || "(none yet)"}.\n` +
    `Categories: ${cats.join(", ")}.\nColours: ${colors.join(", ")}.`) +
    (_notes ? "\nCustomer's standing instructions (apply when filling params): " + _notes : "");
  const recent = history.slice(-21, -1).map((t) => ({ role: t.role, content: t.content }));
  const r = await brainCall<ServerChat>("chat", { message: text + (await maybeWebContext(text)), actions: CHAT_ACTIONS, context, history: recent, scope: CLOSET_SCOPE, assist: true }, onChunk);
  const say = asStr(r.reply) ?? "";
  // The brain returns an ARRAY of actions (multi-item in one message → one action each). Map EACH
  // server action to a resolved on-device ChatAction. Back-compat: a single {action, params} still works.
  const list: ServerChatItem[] = Array.isArray(r.actions) && r.actions.length
    ? r.actions
    : (r.action ? [{ action: r.action, params: r.params }] : []);
  const mapped: ChatAction[] = [];
  for (const it of list) {
    const single = await toAction({ action: it.action ?? it.name ?? "", params: it.params, reply: "" });
    mapped.push(single.action);
  }
  if (mapped.length === 0) return { reply: say || HELP, actions: [{ kind: "none" }] };
  return { reply: say, actions: mapped };
}

const asStr = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v.trim() : undefined);
const asCents = (v: unknown): number | undefined => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? ""));
  return Number.isFinite(n) && n > 0 ? Math.round(n) : undefined;
};
const asDate = (v: unknown): string | undefined => {
  const s = asStr(v);
  return s && /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : undefined;
};
const asFlag = (v: unknown): boolean => v === true || v === "true";
const none = (message: string): BrainResponse => ({ reply: "", action: { kind: "none", message } });

function pickList(rows: Garment[]): string {
  return rows.slice(0, 5).map((g) => `• ${g.item}${g.brand ? ` (${g.brand})` : ""}`).join("\n");
}

// Resolve an item/brand reference to exactly one local garment, or explain why not.
async function resolveOne(term: string | undefined): Promise<{ row?: Garment; problem?: string }> {
  if (!term) return { problem: "Which item do you mean? Give me its name." };
  const rows = await findByName(term);
  if (rows.length === 0) {
    const all = await store.listGarments(50000);
    return { problem: notFoundMessage(all.map((g) => ({ name: garmentName(g) })), term) };
  }
  if (rows.length > 1) return { problem: `I found ${rows.length} matching "${term}" — be more specific:\n${pickList(rows)}` };
  return { row: rows[0] };
}

export async function toAction(r: ServerChat): Promise<BrainResponse> {
  const p = r.params ?? {};
  const say = asStr(r.reply) ?? "";

  // ── ARTIFACT TOOLS (the library) — handled EARLY, above any app-specific
  // precondition (no vehicle / no plant / nothing selected). The library belongs
  // to the customer, not to a currently-active record. (Garage shipped this bug:
  // its no-vehicle guard silently swallowed every library action, 2026-08-13.)
  if (r.action === "library_save") {
    const _content = asStr(p.content);
    if (!_content) return none("Tell me what to save — or ask me to write it first and I'll keep it.");
    const _rawKind = (asStr(p.item_kind) ?? "note").toLowerCase();
    const _itemKind = _rawKind === "lesson" || _rawKind === "plan" ? _rawKind : "note";
    return { reply: say, action: { kind: "library_save", title: asStr(p.title) ?? "Untitled", itemKind: _itemKind, content: _content, folder: asStr(p.folder) } };
  }
  if (r.action === "library_list") return { reply: say, action: { kind: "library_list", folder: asStr(p.folder) } };
  if (r.action === "library_remove") {
    const _r = resolveLibraryItem(asStr(p.title));
    if (!_r.item) return none(_r.problem!);
    return { reply: say, action: { kind: "library_remove", id: _r.item.id, label: `"${_r.item.title}"` } };
  }
  if (r.action === "library_move") {
    const _folder = asStr(p.folder);
    if (!_folder) return none("Which folder should it go to?");
    const _r2 = resolveLibraryItem(asStr(p.title));
    if (!_r2.item) return none(_r2.problem!);
    return { reply: say, action: { kind: "library_move", id: _r2.item.id, folder: _folder, label: `"${_r2.item.title}"` } };
  }
  if (r.action === "library_merge") {
    const _titles = (asStr(p.titles) ?? "").split("|").map((s) => s.trim()).filter(Boolean);
    if (_titles.length < 2) return none("Tell me which two (or more) items to merge, by title.");
    const _ids: string[] = [];
    for (const _t of _titles) {
      const _r3 = resolveLibraryItem(_t);
      if (!_r3.item) return none(_r3.problem!);
      _ids.push(_r3.item.id);
    }
    return { reply: say, action: { kind: "library_merge", ids: _ids, title: asStr(p.new_title) ?? _titles.join(" + "), label: _titles.map((t) => `"${t}"`).join(" + ") } };
  }
  switch (r.action) {
    case "add_garment": {
      const item = asStr(p.item);
      if (!item) return none(say || 'What should I add? Try "add a black leather jacket for 129.99".');
      const category = asStr(p.category);
      const color = asStr(p.color);
      return {
        reply: say,
        action: {
          kind: "add_garment",
          item,
          brand: asStr(p.brand) ?? null,
          category: category ? normalizeLower(category) : null,
          color: color ? normalizeLower(color) : null,
          price_cents: asCents(p.price_cents),
          purchased_on: asDate(p.purchased_on) ?? null,
        },
      };
    }
    case "log_wear": {
      const { row, problem } = await resolveOne(asStr(p.item));
      if (!row) return none(problem!);
      return { reply: say, action: { kind: "log_wear", garmentId: row.id, label: row.item } };
    }
    case "edit_garment": {
      const { row, problem } = await resolveOne(asStr(p.item));
      if (!row) return none(problem!);
      const patch: GarmentPatch = {};
      const newItem = asStr(p.set_item);
      if (newItem) patch.item = newItem;
      const brand = asStr(p.set_brand);
      if (brand) patch.brand = brand;
      const cat = asStr(p.set_category);
      if (cat) patch.category = normalizeLower(cat);
      const color = asStr(p.set_color);
      if (color) patch.color = normalizeLower(color);
      const cents = asCents(p.set_price_cents);
      if (cents) patch.price_cents = cents;
      const date = asDate(p.set_purchased_on);
      if (date) patch.purchased_on = date;
      if (Object.keys(patch).length === 0) return none(say || "What should I change on it — name, brand, category, colour, price, or purchase date?");
      return { reply: say, action: { kind: "edit_garment", id: row.id, patch, label: row.item } };
    }
    case "delete_garment": {
      const { row, problem } = await resolveOne(asStr(p.item));
      if (!row) return none(problem!);
      return { reply: say, action: { kind: "delete_garment", id: row.id, label: row.item } };
    }
    case "filter": {
      const filter: ClosetFilter = {};
      const textQ = asStr(p.text);
      if (textQ) filter.text = textQ.toLowerCase(); // applyClosetFilter matches lowercase
      const cat = asStr(p.category);
      if (cat) filter.category = normalizeLower(cat);
      const color = asStr(p.color);
      if (color) filter.color = normalizeLower(color);
      if (asFlag(p.never_worn)) filter.neverWorn = true;
      else if (asFlag(p.worn)) filter.worn = true;
      if (Object.keys(filter).length === 0) return none(say || "What should I filter by — a name, category, colour, or worn/never-worn?");
      return { reply: "", action: { kind: "filter", filter, label: say || undefined } };
    }
    case "summarize":
      return { reply: say, action: { kind: "summarize" } };
    case "backup_save":
      return { reply: say, action: { kind: "backup_save" } };
    case "backup_status":
      return { reply: say, action: { kind: "backup_status" } };
    case "backup_restore": {
      const prov = asStr(p.provider);
      return { reply: say, action: { kind: "backup_restore", provider: prov === "github" || prov === "gdrive" ? prov : undefined } };
    }
    case "save_local_copy":
      return { reply: say, action: { kind: "save_local_copy" } };
    case "import_file":
      return { reply: say, action: { kind: "import_file" } };
    case "compose_outfit":
      return { reply: say, action: { kind: "compose_outfit", occasion: asStr(p.occasion) } };
    case "save_to_cloud": {
      const ck = asStr(p.content_kind) ?? "";
      const contentKind = /conversation|chat|messages|prompts/i.test(ck) ? "conversation" : "note";
      const dest = asStr(p.destination);
      const destination = dest ? (/drive|gdrive|google/i.test(dest) ? "gdrive" : /git\s*hub/i.test(dest) ? "github" : undefined) : undefined;
      return { reply: say, action: { kind: "save_to_cloud", contentKind, note: asStr(p.note) ?? asStr(p.content), destination } };
    }
    case "remember": {
      const instruction = asStr(p.instruction);
      if (!instruction) return none(say || "What should I remember?");
      return { reply: say, action: { kind: "remember", instruction } };
    }
    default:
      return none(say || HELP);
  }
}

// ---- LOCAL INTENT-ROUTER (the free fallback) --------------------------------

/** Local heuristic: turn "add a black jacket for 129.99" into a garment draft. */
function parseGarment(raw: string): {
  item: string; brand?: string | null; category?: string | null; color?: string | null; price_cents?: number;
} {
  let text = raw.trim();
  let price_cents = 0;
  const pm = text.match(/(?:for|at|costs?|priced?|£|\$|€)\s*[$£€]?\s*(\d+(?:[.,]\d{1,2})?)/i);
  if (pm) {
    price_cents = priceToCents(pm[1].replace(",", "."));
    text = text.replace(pm[0], " ").trim();
  }
  const low = ` ${text.toLowerCase()} `;
  const color = DEFAULT_COLORS.find((c) => low.includes(` ${c} `)) ?? null;
  const category = DEFAULT_CATEGORIES.find((c) => low.includes(` ${c} `)) ?? null;
  const item = text.replace(/\s+/g, " ").trim() || raw.trim();
  return { item, color, category, price_cents: price_cents || undefined };
}

/** Local heuristic: turn a query into a structured wardrobe filter. */
function buildFilter(text: string): ClosetFilter {
  const low = ` ${text.toLowerCase().replace(/['?.!,]/g, " ")} `;
  const f: ClosetFilter = {};
  if (/\b(never\s*worn|not\s*worn|unworn)\b/.test(low)) f.neverWorn = true;
  else if (/\bworn\b/.test(low)) f.worn = true;
  const color = DEFAULT_COLORS.find((c) => low.includes(` ${c} `));
  if (color) f.color = color;
  const cat = DEFAULT_CATEGORIES.find((c) => low.includes(` ${c} `));
  if (cat) f.category = cat;
  let rest = low;
  const stop = ["show", "find", "list", "search", "which", "filter", "how", "many", "all", "by", "what",
    "me", "my", "the", "a", "an", "item", "items", "garment", "garments", "clothes", "clothing",
    "worn", "never", "not", "unworn", "in", "with", "of", "that", "are", "is", "do", "i", "have", "havent"];
  for (const w of stop) rest = rest.replace(new RegExp(`\\b${w}\\b`, "g"), " ");
  if (color) rest = rest.split(color).join(" ");
  if (cat) rest = rest.split(cat).join(" ");
  rest = rest.replace(/\s+/g, " ").trim();
  if (rest.length >= 2) f.text = rest;
  return f;
}

export async function routeMessage(text: string): Promise<BrainResponse> {
  const t = text.trim();
  if (!t) return { reply: "", action: { kind: "none", message: HELP } };
  const low = t.toLowerCase();

  // Summarize / recap — the ONE existing AI feature.
  if (/\b(summar(?:y|ise|ize)|overview|recap|report)\b/.test(low)) {
    return { reply: "", action: { kind: "summarize" } };
  }

  // Add a garment.
  const addM = t.match(/^(?:add|create|new)\s+(?:an?\s+)?(.+)$/i);
  if (addM) {
    return { reply: "", action: { kind: "add_garment", ...parseGarment(addM[1]) } };
  }

  // Log a wear.
  const wearM = t.match(/^(?:i\s+)?(?:wore|wear|log(?:ged)?(?:\s+a)?\s+wear\s+(?:for\s+)?|worn)\s+(?:my\s+|the\s+|a\s+)?(.+)$/i);
  if (wearM) {
    const rows = await findByName(wearM[1]);
    if (rows.length === 1) return { reply: "", action: { kind: "log_wear", garmentId: rows[0].id, label: rows[0].item } };
    if (rows.length === 0) return { reply: "", action: { kind: "none", message: `Couldn't find "${wearM[1].trim()}".` } };
    return { reply: "", action: { kind: "none", message: "Several items match — be more specific." } };
  }

  // Rename / edit an item's name.
  const renM = t.match(/^(?:rename|edit)\s+(.+?)\s+to\s+(.+)$/i);
  if (renM) {
    const rows = await findByName(renM[1]);
    const newName = renM[2].trim();
    if (rows.length === 1) return { reply: "", action: { kind: "edit_garment", id: rows[0].id, patch: { item: newName }, label: rows[0].item } };
    if (rows.length === 0) return { reply: "", action: { kind: "none", message: `Couldn't find "${renM[1].trim()}".` } };
    return { reply: "", action: { kind: "none", message: "Several items match — be more specific." } };
  }

  // Delete (Human-in-the-loop confirm happens in Chat.tsx).
  const delM = t.match(/^(?:delete|remove)\s+(?:the\s+)?(.+)$/i);
  if (delM) {
    const rows = await findByName(delM[1]);
    if (rows.length === 1) return { reply: "", action: { kind: "delete_garment", id: rows[0].id, label: rows[0].item } };
    if (rows.length === 0) return { reply: "", action: { kind: "none", message: `Couldn't find "${delM[1].trim()}".` } };
    return { reply: "", action: { kind: "none", message: "Several items match — be more specific." } };
  }

  // Filter / search.
  if (QUERY_RE.test(low)) {
    return { reply: "", action: { kind: "filter", filter: buildFilter(t), label: t } };
  }

  return { reply: "", action: { kind: "none", message: HELP } };
}

// Resolve a library title to exactly one item, or explain why not (same 0/1/many
// contract the app uses for its own entries).
function resolveLibraryItem(term: string | undefined): { item?: import("../library").LibraryItem; problem?: string } {
  if (!term) return { problem: "Which saved item do you mean? Give me its title." };
  const found = libraryFind(term);
  if (found.length === 0) return { problem: `I couldn't find "${term}" in your library.` };
  if (found.length > 1) return { problem: `I found ${found.length} matching "${term}" — be more specific: ${found.map((f) => `"${f.title}"`).join(", ")}` };
  return { item: found[0] };
}
